import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Two-step confirm hook. First call to `trigger()` arms the action and
 * starts a disarm timer; a second call within the window invokes
 * `onConfirm`. Used for destructive actions (replace-mode start, cancel
 * a running ingest, roll back a finished run) where we want a soft
 * "are you sure?" without a modal.
 *
 * Why a hook (vs. inlining setState + setTimeout): the timer must be
 * cleared on unmount AND on every re-arm — without that, a stale callback
 * fires `setState` against an unmounted component, and rapid clicks stack
 * timers that race each other.
 */
export function useArmedConfirm(opts: {
  onConfirm: () => void | Promise<void>;
  holdMs?: number;
}): {
  armed: boolean;
  pending: boolean;
  error: string | null;
  trigger: () => void;
  disarm: () => void;
} {
  const { onConfirm, holdMs = 5000 } = opts;
  const [armed, setArmed] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  const disarm = useCallback(() => {
    clearTimer();
    setArmed(false);
  }, [clearTimer]);

  const trigger = useCallback(() => {
    if (pending) return;
    if (!armed) {
      setError(null);
      setArmed(true);
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        setArmed(false);
      }, holdMs);
      return;
    }
    clearTimer();
    setArmed(false);
    setPending(true);
    void Promise.resolve()
      .then(onConfirm)
      .then(() => {
        setError(null);
      })
      .catch((err: unknown) => {
        setError((err as Error).message ?? String(err));
      })
      .finally(() => {
        setPending(false);
      });
  }, [armed, pending, onConfirm, holdMs, clearTimer]);

  return { armed, pending, error, trigger, disarm };
}
